/**
 * HeroVideoTile / HeroVideoTiles
 * ---------------------------------
 * Zwei Video-Teaser-Kacheln direkt neben den `HeroChoiceTiles` im
 * `/aktion`-Hero. Zusammen entsteht auf Desktop eine Reihe aus 4
 * Kacheln (2 CTA + 2 Video), auf Mobile werden alle gestacked.
 *
 * Preview-Zustand (Kachel):
 *   - Video autoplay stumm im Loop (`<video muted autoPlay loop
 *     playsInline preload="metadata">`), damit das Bewegtbild sofort
 *     Aufmerksamkeit zieht, aber keine Autoplay-Blocker triggert.
 *   - Titel oben links über einem dunklen Gradient-Overlay.
 *   - Play-Icon oben rechts als visueller Hinweis, dass Klick =
 *     Fullscreen mit Ton auslöst.
 *   - Hover: leichter translateY(-2px) + Gold-Border-Glow.
 *
 * Fullscreen-Modal (Klick):
 *   - `role="dialog"` + `aria-modal="true"`, dunkler Backdrop,
 *     Backdrop-Klick / X-Button / ESC schließen.
 *   - Video startet von `currentTime = 0`, **Ton automatisch AN**
 *     (User-Klick ist eine gültige User-Gesture). Fallback: falls
 *     `play()` mit Sound rejectet, wird stumm weiterversucht.
 *   - Loop bewusst AUS im Modal — `onEnded` feuert das Completed-
 *     Event für Analytics, User entscheidet dann selbst.
 *
 * Analytics:
 *   - `video_tile_open`      → beim Öffnen des Modals
 *   - `video_tile_completed` → wenn das Video im Modal komplett endet
 *   Beide via `trackInteraction` → `sendReliable` (Beacon-first).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import type { VideoTileConfig, HeroVideoTilesConfig } from "@/lib/useAktionsSeite";
import { trackInteraction } from "@/lib/analytics";

/** Wählt die passende Quelle abhängig vom Wunsch-Aspect-Ratio,
 *  fällt automatisch auf die andere Variante zurück, wenn die
 *  bevorzugte URL leer ist. */
function pickSrc(cfg: VideoTileConfig): string {
    if (cfg.preferred_aspect === "21:9") {
        return cfg.video_21x9_url || cfg.video_16x9_url || "";
    }
    return cfg.video_16x9_url || cfg.video_21x9_url || "";
}

/** Rendert eine einzelne Preview-Kachel (klickbar). */
function VideoTilePreview({
    tile, index, onOpen,
}: {
    tile: VideoTileConfig;
    index: number;
    onOpen: () => void;
}) {
    const src = pickSrc(tile);
    const aspect = tile.preferred_aspect === "21:9" ? "21 / 9" : "16 / 9";
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`hero-video-tile-${index}`}
            aria-label={`Video ansehen: ${tile.title}`}
            className="group relative w-full md:min-w-[240px] md:max-w-[320px] md:flex-1 text-left rounded-lg overflow-hidden transition-all"
            style={{
                border: "2px solid #0F1830",
                boxShadow: "0 6px 20px -8px rgba(15, 24, 48, 0.4)",
                background: "#0F1830",
                aspectRatio: aspect,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                    "0 12px 28px -8px rgba(15, 24, 48, 0.6), 0 0 0 3px rgba(212, 168, 85, 0.35)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "0 6px 20px -8px rgba(15, 24, 48, 0.4)";
            }}
        >
            {src ? (
                <video
                    src={src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid={`hero-video-tile-${index}-preview`}
                />
            ) : null}
            {/* Top-Gradient für den Titel */}
            <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-16 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(15,24,48,0.85) 0%, rgba(15,24,48,0) 100%)",
                }}
            />
            {/* Titel oben links */}
            <span
                className="absolute left-3 top-2.5 right-14 font-anton uppercase text-sm md:text-base leading-tight text-white break-words"
                style={{ letterSpacing: "0.02em", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
            >
                {tile.title}
            </span>
            {/* Play-Icon oben rechts */}
            <span
                aria-hidden
                className="absolute right-2 top-2 inline-flex items-center justify-center rounded-full transition-transform group-hover:scale-110"
                style={{
                    width: 34,
                    height: 34,
                    background: "rgba(255,255,255,0.92)",
                    border: "1.5px solid #D4A855",
                    color: "#0F1830",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.35)",
                }}
            >
                <Play size={14} strokeWidth={2.4} fill="#0F1830" style={{ marginLeft: 1 }} />
            </span>
        </button>
    );
}

/** Fullscreen-Modal, das nach Klick geöffnet wird. */
function VideoTileModal({
    tile, index, onClose,
}: {
    tile: VideoTileConfig;
    index: number;
    onClose: () => void;
}) {
    const src = pickSrc(tile);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // ESC schließt.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Body-Scroll sperren, solange das Modal offen ist.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    // Video von 0 starten, mit Ton. User-Klick ist eine gültige Gesture,
    // moderne Browser erlauben Autoplay mit Sound. Fallback: falls es
    // trotzdem rejectet, versuchen wir es stumm weiter (User kann via
    // Native-Controls den Ton reaktivieren).
    //
    // Wichtig: KEIN autoPlay-Attribut am `<video>`-Element — sonst
    // startet der Browser die Wiedergabe parallel zu unserem `.play()`
    // Aufruf und React's Dev-Error-Overlay fängt spurious MediaError-
    // Events auf ("no supported sources"). Wir steuern den Play-
    // Lifecycle ausschließlich imperativ.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !src) return;
        v.muted = false;
        v.volume = 1;
        const tryPlay = () => {
            try {
                v.currentTime = 0;
            } catch { /* Metadata noch nicht geladen — ignorieren */ }
            const p = v.play();
            if (p && typeof (p as Promise<void>).catch === "function") {
                (p as Promise<void>).catch(() => {
                    // Autoplay mit Sound abgelehnt (Browser-Policy) → stumm
                    // starten. Zweiten Reject bewusst schlucken, damit kein
                    // ungefangenes Promise-Reject in der Konsole landet.
                    v.muted = true;
                    v.play().catch(() => { /* swallow */ });
                });
            }
        };
        // Wenn der Browser noch keine Metadaten hat, warten bis er sie hat —
        // sonst wirft `currentTime = 0` in einigen Browsern InvalidState und
        // `play()` läuft in ein SrcNotSupported-Race. `readyState >= 1`
        // heißt HAVE_METADATA (Dauer + Dimensionen bekannt).
        if (v.readyState >= 1) {
            tryPlay();
        } else {
            const onLoaded = () => {
                v.removeEventListener("loadedmetadata", onLoaded);
                tryPlay();
            };
            v.addEventListener("loadedmetadata", onLoaded);
            // Explizit `load()` anstoßen, falls das Browser-Preload es
            // noch nicht getan hat (z. B. wenn `preload="metadata"`
            // gerade erst begonnen hat).
            try { v.load(); } catch { /* ignore */ }
        }
    }, [src]);

    const handleBackdrop = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleEnded = () => {
        try {
            trackInteraction("video_tile_completed", { tile_id: index, src });
        } catch { /* nicht kritisch */ }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={tile.title}
            data-testid={`hero-video-tile-${index}-modal`}
            onClick={handleBackdrop}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.9)", backdropFilter: "blur(6px)" }}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Video schließen"
                data-testid={`hero-video-tile-${index}-close`}
                className="absolute top-4 right-4 md:top-6 md:right-6 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{
                    width: 40,
                    height: 40,
                    background: "rgba(255,255,255,0.95)",
                    color: "#0F1830",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.5)",
                    border: "1.5px solid #FFFFFF",
                }}
            >
                <X size={22} strokeWidth={2.4} />
            </button>
            {src ? (
                <video
                    ref={videoRef}
                    src={src}
                    playsInline
                    controls
                    onEnded={handleEnded}
                    className="rounded-md"
                    style={{ maxWidth: "90vw", maxHeight: "85vh", background: "#000" }}
                    data-testid={`hero-video-tile-${index}-video`}
                />
            ) : null}
        </div>
    );
}

type Props = { cfg: HeroVideoTilesConfig };

/**
 * Master-Component: rendert 0–2 Kacheln + steuert das Modal.
 * Wenn `cfg.enabled = false` oder keine Kachel selbst enabled ist,
 * wird `null` gerendert (kein leerer Wrapper, kein Layout-Spacing).
 */
export default function HeroVideoTiles({ cfg }: Props) {
    const [openTile, setOpenTile] = useState<1 | 2 | null>(null);

    const handleOpen = useCallback((idx: 1 | 2, tile: VideoTileConfig) => {
        try {
            trackInteraction("video_tile_open", { tile_id: idx, src: pickSrc(tile) });
        } catch { /* nicht kritisch */ }
        setOpenTile(idx);
    }, []);

    if (!cfg?.enabled) return null;

    const active: Array<{ idx: 1 | 2; tile: VideoTileConfig }> = [];
    if (cfg.tile_1?.enabled) active.push({ idx: 1, tile: cfg.tile_1 });
    if (cfg.tile_2?.enabled) active.push({ idx: 2, tile: cfg.tile_2 });
    if (active.length === 0) return null;

    const activeTile =
        openTile === 1 ? cfg.tile_1 : openTile === 2 ? cfg.tile_2 : null;

    return (
        <div
            className="flex flex-col md:flex-row gap-3 w-full md:w-auto"
            data-testid="hero-video-tiles"
        >
            {active.map(({ idx, tile }) => (
                <VideoTilePreview
                    key={idx}
                    tile={tile}
                    index={idx}
                    onOpen={() => handleOpen(idx, tile)}
                />
            ))}
            {activeTile && openTile !== null && (
                <VideoTileModal
                    tile={activeTile}
                    index={openTile}
                    onClose={() => setOpenTile(null)}
                />
            )}
        </div>
    );
}
